import type { ReactNode } from "react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

import { ViewerBackButton, ViewerTopBar } from "@/components/layout/viewer-top-bar";
import { ViewerMetaChip } from "@/components/semantic-badges";
import { ExportButton } from "@/features/exports/export-button";
import { PacketSourceSheetButton } from "@/features/document-viewport/packet-source-sheet-button";
import {
  ViewerPager,
  ViewerSourceSelect,
} from "@/features/document-viewport/viewer-source-controls";
import type { ViewerSource } from "@/types/takeoff-pilot";

interface TakeoffViewerHeaderProps {
  /** Opens the shared Export dialog. */
  onExport: () => void;
  isExportInProgress: boolean;
  /** Job name. */
  title: string;
  /** Linked schedule revision, or "No schedule". */
  subtitle?: ReactNode;
  /** Shows the view-only marker for reviewers. */
  readOnly?: boolean;
  viewerSources: ViewerSource[];
  selectedPlanDocumentId: string;
  onSelectPlanDocumentId: (value: string) => void;
  onBack: () => void;
  tagsCount?: number;
  activePage?: number;
  pageCount?: number;
  onGoToPage?: (page: number) => void;
  /** Revision shown in the viewport; packet pages link back to their source sheets. */
  revisionId?: Id<"projectDocumentRevisions">;
  pageLabels?: ReadonlyMap<number, string>;
  /** Undo/redo and toolset buttons. */
  toolsets?: ReactNode;
}

export function TakeoffViewerHeader({
  title,
  subtitle,
  readOnly = false,
  viewerSources,
  selectedPlanDocumentId,
  onSelectPlanDocumentId,
  onBack,
  tagsCount = 0,
  activePage = 1,
  pageCount = 1,
  onGoToPage,
  revisionId,
  pageLabels,
  toolsets,
  onExport,
  isExportInProgress,
}: TakeoffViewerHeaderProps) {
  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-[60] flex">
      <ViewerTopBar
        className="pointer-events-auto"
        back={<ViewerBackButton label="Back to TakeoffPilot" onBack={onBack} />}
        title={title}
        subtitle={subtitle}
        readOnly={readOnly}
        source={
          <>
            <ViewerSourceSelect
              sources={viewerSources}
              value={selectedPlanDocumentId}
              onValueChange={onSelectPlanDocumentId}
            />
            {onGoToPage ? (
              <ViewerPager
                activePage={activePage}
                pageCount={pageCount}
                onGoToPage={onGoToPage}
                pageLabels={pageLabels}
              />
            ) : null}
            {revisionId ? (
              <PacketSourceSheetButton revisionId={revisionId} pageNumber={activePage} />
            ) : null}
          </>
        }
        meta={
          tagsCount > 0 ? (
            <ViewerMetaChip className="hidden sm:inline-flex">
              {tagsCount} tag{tagsCount === 1 ? "" : "s"}
            </ViewerMetaChip>
          ) : null
        }
        tools={toolsets}
        actions={<ExportButton onClick={onExport} busy={isExportInProgress} />}
      />
    </div>
  );
}
